import React, { useState, useEffect, useRef } from "react";

const GCC_COUNTRIES = [
  {
    name: "Saudi Arabia",
    code: "966",
    flag: (
      <img src="https://flagcdn.com/sa.svg" width="25" alt="Saudi Arabia" />
    ),
  },
  {
    name: "United Arab Emirates",
    code: "971",
    flag: <img src="https://flagcdn.com/ae.svg" width="25" alt="UAE" />,
  },
  {
    name: "Kuwait",
    code: "965",
    flag: <img src="https://flagcdn.com/kw.svg" width="25" alt="Kuwait" />,
  },
  {
    name: "Qatar",
    code: "974",
    flag: <img src="https://flagcdn.com/qa.svg" width="25" alt="Qatar" />,
  },
  {
    name: "Bahrain",
    code: "973",
    flag: <img src="https://flagcdn.com/bh.svg" width="25" alt="Bahrain" />,
  },
  {
    name: "Oman",
    code: "968",
    flag: <img src="https://flagcdn.com/om.svg" width="25" alt="Oman" />,
  },
  {
    name: "Algeria",
    code: "213",
    flag: <img src="https://flagcdn.com/dz.svg" width="25" alt="Algeria" />,
  },
  {
    name: "Egypt",
    code: "20",
    flag: <img src="https://flagcdn.com/eg.svg" width="25" alt="Egypt" />,
  },
];

interface Country {
  name: string;
  code: string;
  flag: JSX.Element;
}

interface DropdownProps {
  options: Country[];
  onSelect: (code: string) => void;
  isVisible: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  onSelect,
  isVisible,
}) => {
  return (
    <div
      className={`absolute bg-white dark:bg-gray-800/95 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700/50 backdrop-blur-sm w-64 ${
        isVisible ? "block" : "hidden"
      }`}
      style={{ zIndex: 999999, top: "calc(100% + 0.5rem)" }}
    >
      <ul className="py-2 max-h-[280px] overflow-y-auto">
        {options.map((option) => (
          <li key={option.code}>
            <button
              type="button"
              className="w-full px-4 py-2.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors flex items-center gap-3"
              onClick={() => onSelect(option.code)}
            >
              <div className="w-8 flex-shrink-0">{option.flag}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 dark:text-gray-200 truncate">{option.name}</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">+{option.code}</p>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

interface LoginPhoneInputProps {
  value: string;
  onChange: (phoneNumber: string) => void;
  disabled: boolean;
  setSelectedCountry: (code: string) => void;
  inputMode:
    | "none"
    | "text"
    | "tel"
    | "url"
    | "email"
    | "numeric"
    | "decimal"
    | "search";
  pattern?: string;
}

const LoginPhoneInput: React.FC<LoginPhoneInputProps> = ({
  value,
  onChange,
  disabled,
  setSelectedCountry,
  inputMode,
  pattern,
}) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [selectedCountry, setCountry] = useState(GCC_COUNTRIES[0].code);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Notify parent of initial country selection
  useEffect(() => {
    setSelectedCountry(selectedCountry);
  }, [setSelectedCountry, selectedCountry]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    if (!disabled) {
      setDropdownVisible(!dropdownVisible);
    }
  };

  const selectCountry = (countryCode: string) => {
    setCountry(countryCode);
    setSelectedCountry(countryCode);
    setDropdownVisible(false);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phoneNumber = e.target.value.replace(/^0+/, "");
    onChange(phoneNumber);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex">
        <button
          type="button"
          onClick={toggleDropdown}
          disabled={disabled}
          className={`
            flex items-center gap-2 px-4 py-3 rounded-l-xl border
            ${
              disabled
                ? "bg-gray-100 dark:bg-gray-800/50 border-gray-300 dark:border-gray-700/50 cursor-not-allowed"
                : "bg-gray-50 dark:bg-gray-700/30 border-gray-300 dark:border-gray-600/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:focus:border-gray-500/50"
            }
            transition-colors
            focus:outline-none
          `}
        >
          <div className="w-6">
            {
              GCC_COUNTRIES.find((country) => country.code === selectedCountry)
                ?.flag
            }
          </div>
          <span className="text-gray-700 dark:text-gray-200">+{selectedCountry}</span>
          <svg
            className="w-4 h-4 text-gray-500 dark:text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <input
          type="text"
          className={`
            flex-1 px-4 py-3 rounded-r-xl border-y border-r
            ${
              disabled
                ? "bg-gray-100 dark:bg-gray-800/50 border-gray-300 dark:border-gray-700/50 text-gray-400 cursor-not-allowed"
                : "bg-gray-50 dark:bg-gray-700/30 border-gray-300 dark:border-gray-600/50 text-gray-900 dark:text-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:focus:border-gray-500/50"
            }
            placeholder-gray-500 dark:placeholder-gray-400
            transition-colors
            focus:outline-none
          `}
          placeholder="34460630"
          required
          value={value}
          onChange={handlePhoneChange}
          disabled={disabled}
          inputMode={inputMode}
          pattern={pattern}
        />
      </div>

      <Dropdown
        options={GCC_COUNTRIES}
        onSelect={selectCountry}
        isVisible={dropdownVisible}
      />
    </div>
  );
};

export default LoginPhoneInput;
